import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import config from "../../../configuration/config";
import MaintenanceCalendarDialog from "../../../components/MaintenanceCalendarDialog";
import PhoneDropDown from "../../../components/_input/_phoneDropDown";
import { getData, User_Data_Key } from "../../../utils/localStorageManager";
import * as journifyTrackingCarEvents from "../../../services/trackingCarEvents";

const PERIODIC_MAINTENANCE_ID = "EB199B7E-55BE-4AC8-B465-9A110B415CA8";

const getServiceIcon = (title) => {
    if (!title) return `url("/assets/icons/services/general.svg")`;
    const lower = title.toLowerCase();
    if (lower.includes("body") || lower.includes("paint")) return `url("/assets/icons/services/painting.svg")`;
    if (lower.includes("periodic")) return `url("/assets/icons/services/maintenance-periodic.svg")`;
    if (lower.includes("mechanic")) return `url("/assets/icons/services/mechanics.svg")`;
    if (lower.includes("diagnosis")) return `url("/assets/icons/services/diagonise.svg")`;
    return `url("/assets/icons/services/general.svg")`;
};

/**
 * Compact step-based booking card replacing the modal popup flow.
 * Steps: Service → Vehicle (brand/year/model) → Date/Time/Note → Submit (success).
 */
function InlineBookingCard({ mainServices = [], preselectedServiceId = null, onConsumePreselect }) {
    const { t: trans, i18n } = useTranslation(["info", "form", "eparts"]);
    const lang = i18n.language || "ar";

    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [resultOrderID, setResultOrderID] = useState(null);
    const [errors, setErrors] = useState({});

    // Service selection
    const [selectedService, setSelectedService] = useState(null);
    const [serviceList, setServiceList] = useState([]);
    const [serviceID, setServiceID] = useState(null);

    // Sub-service-1 selection — surfaces when a main service has 2+ sub1
    // options (e.g. body+paint → سمكره / دهان). Mirrors the popup's behavior
    // in serviceOrderPopup.jsx (the SubService1.length > 1 dropdown).
    const [subServices1, setSubServices1] = useState([]);
    const [selectedSub1, setSelectedSub1] = useState(null);
    const [loadingSub1, setLoadingSub1] = useState(false);

    // Vehicle settings (brands/groups/models)
    const [settings, setSettings] = useState({ brands: [], groups: [], models: [], productModels: [] });
    const [brand, setBrand] = useState(null);
    const [year, setYear] = useState(null);
    const [group, setGroup] = useState(null);
    const [type, setType] = useState(null);
    const [modelCode, setModelCode] = useState("");
    const [vModelCode, setVModelCode] = useState({});

    const [filteredGroups, setFilteredGroups] = useState([]);
    const [filteredModels, setFilteredModels] = useState([]);

    // Registered-vehicle selection
    const [activeVehicleTab, setActiveVehicleTab] = useState("myCars");
    const [userVehicles, setUserVehicles] = useState([]);
    const [selectedVehicleVIN, setSelectedVehicleVIN] = useState("");

    // Branches & schedule
    const [branches, setBranches] = useState([]);
    const [siteID, setSiteID] = useState(null);
    const [date, setDate] = useState(null);
    const [time, setTime] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [availableHours, setAvailableHours] = useState([]);
    const [note, setNote] = useState("");
    const [meterReading, setMeterReading] = useState("");

    // Contact — pre-filled from the logged-in user's profile when available
    // so the backend never receives empty FirstName/LastName/phone/Email.
    const isLogin = !!getData("isAuthenticated");
    const userProfile = useMemo(() => {
        if (!isLogin) return null;
        try { return JSON.parse(getData(User_Data_Key, true)); } catch { return null; }
    }, [isLogin]);
    const profileFirstName = userProfile?.firstNameAr || userProfile?.firstNameEn || userProfile?.firstName || "";
    const profileLastName = userProfile?.lastNameAr || userProfile?.lastNameEn || userProfile?.lastName || "";
    const profilePhone = userProfile?.phone || userProfile?.phoneNumber || "";
    const profileEmail = userProfile?.email || "";
    const [firstName, setFirstName] = useState(profileFirstName);
    const [lastName, setLastName] = useState(profileLastName);
    const [phone, setPhone] = useState(profilePhone || null);
    const [codephone, setCodephone] = useState(null);
    const [email, setEmail] = useState(profileEmail);

    // Keep contact fields in sync if the profile resolves after first render
    useEffect(() => {
        if (profileFirstName && !firstName) setFirstName(profileFirstName);
        if (profileLastName && !lastName) setLastName(profileLastName);
        if (profilePhone && !phone) setPhone(profilePhone);
        if (profileEmail && !email) setEmail(profileEmail);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileFirstName, profileLastName, profilePhone, profileEmail]);

    // ============== fetch settings on mount ==============
    // Mirrors the fetch in serviceOrderPopup.jsx exactly:
    //   1. Hydrate from the HJWeb cache (instant first paint)
    //   2. Then fetch fresh from /{lang}/api/Maintenance/Settings via axios.request
    //   3. Persist the fresh copy back into the cache
    useEffect(() => {
        let cancelled = false;

        function applySettings(data) {
            if (!data) return;
            setSettings({
                brands: data?.brands || [],
                groups: data?.groups || [],
                models: data?.models || [],
                productModels: data?.productModels || [],
            });
            const codes = {};
            (data?.productModels || []).forEach((m) => {
                if (m.ModelCode) {
                    codes[m.productGroupID] = m.ModelCode;
                    codes[m.productTypeID] = m.ModelCode;
                }
            });
            setVModelCode(codes);
        }

        async function fetchSettings() {
            // 1. Try the cache first
            try {
                if ("caches" in window) {
                    const cacheStorage = await caches.open("HJWeb");
                    const cachedResponse = await cacheStorage.match("Settings-" + lang);
                    const cached = await cachedResponse?.json();
                    if (cached && !cancelled) applySettings(cached);
                }
            } catch (e) { /* cache failure is non-fatal */ }

            // 2. Fetch fresh — same call shape as serviceOrderPopup.jsx
            try {
                const settingResponse = await axios.request({
                    baseURL: "/",
                    url: config.controllURL + lang + "/api/Maintenance/Settings",
                    method: "get",
                });
                if (cancelled) return;
                applySettings(settingResponse.data);

                // 3. Persist the fresh copy back
                if ("caches" in window) {
                    const cacheStorage = await caches.open("HJWeb");
                    cacheStorage.put(
                        "Settings-" + lang,
                        new Response(JSON.stringify(settingResponse.data))
                    );
                }
            } catch (e) {
                console.error("Maintenance/Settings fetch failed:", e);
            }
        }

        fetchSettings();
        return () => { cancelled = true; };
    }, [lang]);

    // Fetch branches once
    useEffect(() => {
        axios.post(`${config.controllURL}${lang}/api/Pages/site_FetchMaintenanceBranches`, {})
            .then((res) => {
                setBranches(res.data || []);
                if (res.data?.length === 1) setSiteID(res.data[0].SiteID);
            })
            .catch((e) => {
                console.error("site_FetchMaintenanceBranches failed:", e);
            });
    }, [lang]);

    // Fetch the user's registered vehicles when logged in
    useEffect(() => {
        if (!isLogin) {
            setUserVehicles([]);
            setActiveVehicleTab("newCar");
            return;
        }
        const _userID = userProfile?.id?.toString().split("_")[1];
        if (!_userID) {
            setUserVehicles([]);
            return;
        }
        axios.post(
            `${config.controllURL}${lang}/api/Pages/SiteGetUserCars`,
            { userID: Number(_userID) }
        )
            .then((res) => {
                const list = Array.isArray(res?.data) ? res.data : [];
                setUserVehicles(list);
                if (list.length === 0) setActiveVehicleTab("newCar");
            })
            .catch((e) => {
                console.error("SiteGetUserCars failed:", e);
                setUserVehicles([]);
                setActiveVehicleTab("newCar");
            });
    }, [isLogin, userProfile, lang]);

    const handleSelectMyVehicle = (vin) => {
        if (!vin) {
            setSelectedVehicleVIN("");
            setBrand(null); setYear(null); setGroup(null); setType(null); setModelCode("");
            return;
        }
        const v = userVehicles.find((x) => (x.CarID || x.SN) === vin);
        if (!v) return;
        setSelectedVehicleVIN(vin);
        setBrand(v.BrandID || null);
        setYear(v.Model || null);
        setGroup(v.ProductGroupID || null);
        const t = v.Type || v.ProductTypeID || null;
        setType(t);
        const mc = v.ModelCode || (t && vModelCode[t]) || "";
        setModelCode(mc);
    };

    const handleVehicleTabChange = (tab) => {
        setActiveVehicleTab(tab);
        setSelectedVehicleVIN("");
        setBrand(null); setYear(null); setGroup(null); setType(null); setModelCode("");
        setFilteredGroups([]); setFilteredModels([]);
    };

    // React to externally-preselected service (e.g. user clicked a service card).
    // Routes through handleSelectService so the sub-service-1 picker still
    // appears for multi-branch services (body+paint).
    useEffect(() => {
        if (!preselectedServiceId || !mainServices?.length) return;
        const match = mainServices.find((s) => s.MainServiceID === preselectedServiceId);
        if (match) {
            handleSelectService(match);
            onConsumePreselect && onConsumePreselect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselectedServiceId, mainServices, onConsumePreselect]);

    // ============== service selection handlers ==============
    // Mirrors the popup's behavior for services where the main GUID doesn't
    // map directly to a row in ServiceMaster (e.g. body+paint). When the
    // direct fetch returns nothing, we walk sub-service-1 → sub-service-2
    // → site_FetchServices to land on a real ServiceID the SP can resolve.
    const SUB_SERVICE_PREFERENCE = {
        // sub1 GUID → preferred sub2 GUID (matches DynamicOptions in serviceOrderPopup.jsx)
        "22d24d95-04cd-4c88-88ec-317d940231e0": "bb3a0cda-5601-430b-9bf8-7e523e5eb7a5", // painting → online painting
        "5d22681e-6233-459c-92c0-d576141a7237": "fcaa28c6-de81-47a6-83a9-204d5b26a5ad", // body → online denting
        "a4df3008-2236-4455-854b-da6ddd592364": "8fedd3fb-03cf-4919-9b7e-492f16d913b9", // checking → online checking
        "619b5424-1a20-4e01-950a-a8d87f9f1c7e": "d9af24f0-9932-4481-8136-403471ed6b84", // general repair → online
    };

    const fetchServicesForVehicle = async (mc) => {
        setServiceList([]);
        setServiceID(null);
        if (!mc || !selectedService) return;

        const url = `${config.controllURL}${lang}/api/Pages/site_FetchServices`;
        const tryFetch = async (guid) => {
            try {
                const res = await axios.post(url, { GUID: guid, ModelCode: mc });
                return res.data || [];
            } catch { return []; }
        };

        // 1) Direct: most services (periodic / mechanic / general / checking)
        let services = await tryFetch(selectedService.MainServiceID);

        // 2) Walk sub-services if direct returned nothing (body+paint).
        //    When the user has explicitly picked a sub1 (سمكره / دهان), only
        //    walk that branch — otherwise iterate the full list as before.
        if (services.length === 0) {
            try {
                let sub1List = [];
                if (selectedSub1) {
                    sub1List = [selectedSub1];
                } else {
                    const sub1Res = await axios.post(
                        `${config.controllURL}${lang}/api/Pages/site_fetchMntc_SubService1`,
                        { MainID: selectedService.MainServiceID }
                    );
                    sub1List = sub1Res.data || [];
                }
                for (const sub1 of sub1List) {
                    const sub2Res = await axios.post(
                        `${config.controllURL}${lang}/api/Pages/site_fetchMntc_SubService2`,
                        { SubID: sub1.SubService1ID }
                    );
                    const sub2List = sub2Res.data || [];
                    if (!sub2List.length) continue;
                    // Prefer the dynamic-option mapping (online variant) when defined,
                    // otherwise fall back to the first sub2.
                    const preferredId = SUB_SERVICE_PREFERENCE[sub1.SubService1ID];
                    const chosenSub2 = (preferredId && sub2List.find(s => s.SubService2ID === preferredId))
                        || sub2List[0];
                    services = await tryFetch(chosenSub2.SubService2ID);
                    if (services.length > 0) break;
                }
            } catch (e) {
                console.error("Sub-service walk failed:", e);
            }
        }

        setServiceList(services);
        if (services.length >= 1) setServiceID(services[0].ServiceID);
    };

    useEffect(() => {
        if (modelCode) fetchServicesForVehicle(modelCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelCode, selectedService, selectedSub1]);

    // Pick a main service, then either advance to step 2 directly or stay on
    // step 1 with a sub-service picker (when the main service has 2+ sub1
    // entries, e.g. body+paint → سمكره / دهان).
    const handleSelectService = async (svc) => {
        setSelectedService(svc);
        setSelectedSub1(null);
        setSubServices1([]);
        setLoadingSub1(true);
        try {
            const sub1Res = await axios.post(
                `${config.controllURL}${lang}/api/Pages/site_fetchMntc_SubService1`,
                { MainID: svc.MainServiceID }
            );
            const list = sub1Res.data || [];
            if (list.length > 1) {
                setSubServices1(list);
            } else {
                if (list.length === 1) setSelectedSub1(list[0]);
                setStep(2);
            }
        } catch (e) {
            console.error("sub-service-1 fetch failed:", e);
            setStep(2);
        } finally {
            setLoadingSub1(false);
        }
    };

    const handleSelectSub1 = (sub1) => {
        setSelectedSub1(sub1);
        setStep(2);
    };

    const handleBackToServices = () => {
        setSelectedService(null);
        setSelectedSub1(null);
        setSubServices1([]);
    };

    const handleSelectBrand = (brandID, withYear = year) => {
        setBrand(brandID);
        setGroup(null);
        setType(null);
        setModelCode("");
        if (withYear) {
            const groups = settings.groups.filter((g) => g.brandID === brandID && g.Year === withYear);
            setFilteredGroups(groups);
        } else {
            setFilteredGroups([]);
        }
    };

    const handleSelectYear = (y) => {
        setYear(y);
        setGroup(null);
        setType(null);
        setModelCode("");
        if (brand) {
            const groups = settings.groups.filter((g) => g.brandID === brand && g.Year === y);
            setFilteredGroups(groups);
        }
    };

    const handleSelectGroup = (g) => {
        setGroup(g);
        setType(null);
        let models = [];
        try {
            const matching = settings.groups.find((x) => x.brandID === brand && x.productGroupID === g && x.Year === year);
            const list = (matching?.ListTreeGroups || g).split(",");
            models = settings.productModels.filter((m) => list.includes(m.productGroupID) && m.Model === year);
        } catch {
            models = settings.productModels.filter((m) => m.productGroupID === g && m.Model === year);
        }
        setFilteredModels(models);
        if (models.length > 0) {
            const t = models[0].productTypeID;
            setType(t);
            setModelCode(vModelCode[t] || vModelCode[g] || "");
        } else {
            setModelCode(vModelCode[g] || "");
        }
    };

    const handleSelectType = (t) => {
        setType(t);
        setModelCode(vModelCode[t] || "");
    };

    // ============== date/time ==============
    const fetchAvailableHours = async (d, subID) => {
        try {
            const res = await axios.post(
                `${config.controllURL}${lang}/api/Pages/web_AvailableReshours`,
                { Resdate: d, ServiceGuid: subID }
            );
            if (res.data?.length) {
                setAvailableHours(res.data.map((v) => parseInt(v.AvailableHour)));
            } else {
                setAvailableHours([]);
            }
        } catch {
            setAvailableHours([]);
            toast.warn(trans("form:please-choose-another-date"));
        }
    };

    const onDatePicked = async (value) => {
        const selected = moment(value).format("YYYY-MM-DD");
        const today = moment().format("YYYY-MM-DD");
        const isToday = selected === today;
        const subServiceID = selectedSub1?.SubService1ID || selectedService?.SubService1ID || selectedService?.MainServiceID;
        const restricted = subServiceID && subServiceID.toUpperCase() !== PERIODIC_MAINTENANCE_ID;

        if (isToday && restricted) {
            toast.warn(lang === "ar"
                ? "هذه الخدمة لا يمكن حجزها في نفس اليوم، يرجى اختيار تاريخ آخر (ابتداءً من الغد)."
                : "This service cannot be booked on same day, please choose another date (starting from tomorrow).");
            return;
        }
        setDate(value);
        setTime(null);
        setAvailableHours([]);
        await fetchAvailableHours(selected, subServiceID);
    };

    // ============== validation ==============
    // Returns the (possibly empty) error map AND updates state. Mirrors the
    // Joi-based validation in serviceOrderPopup.jsx but accounts for fields
    // that the inline card hides when their picker has 0 or 1 option:
    //   - serviceID is optional when serviceList is empty (we fall back to
    //     selectedService.MainServiceID, same as the popup's sub-service path
    //     for body+paint).
    //   - siteID is optional when branches is empty.
    const validateStep = (s) => {
        const e = {};
        if (s === 2) {
            if (selectedVehicleVIN) {
                if (!type) e.type = trans("messages:field_required");
                if (!year) e.year = trans("messages:field_required");
            } else {
                if (!brand) e.brand = trans("messages:field_required");
                if (!year) e.year = trans("messages:field_required");
                if (!group) e.group = trans("messages:field_required");
                if (!type) e.type = trans("messages:field_required");
            }
        }
        if (s === 3) {
            if (serviceList.length > 1 && !serviceID) e.serviceID = trans("messages:field_required");
            if (branches.length > 1 && !siteID) e.siteID = trans("messages:field_required");
            if (!date) e.date = trans("messages:field_required");
            if (!time) e.time = trans("messages:field_required");
            // Backend rejects empty contact fields even for logged-in users,
            // so always require them — they're pre-filled from the profile.
            if (!firstName) e.firstName = trans("messages:field_required");
            if (!lastName) e.lastName = trans("messages:field_required");
            if (!phone) e.phone = trans("messages:field_required");
        }
        setErrors(e);
        return e;
    };

    const handleNext = () => {
        const e = validateStep(step);
        if (Object.keys(e).length === 0) setStep(step + 1);
        else toast.warn(lang === "ar" ? "يرجى استكمال الحقول المطلوبة." : "Please complete the required fields.");
    };

    const handleBack = () => setStep(Math.max(1, step - 1));

    // ============== submit ==============
    // Field label map used to translate validation keys into something the
    // user can act on (toast lists exactly what's missing).
    const fieldLabel = (key) => {
        const map = lang === "ar" ? {
            serviceID: "الخدمة",
            siteID: "الفرع",
            date: "التاريخ",
            time: "الوقت",
            firstName: "الاسم الأول",
            lastName: "اللقب",
            phone: "رقم الجوال",
        } : {
            serviceID: "Service",
            siteID: "Branch",
            date: "Date",
            time: "Time",
            firstName: "First name",
            lastName: "Last name",
            phone: "Phone",
        };
        return map[key] || key;
    };

    const submit = async () => {
        if (submitting) return;
        const e = validateStep(3);
        if (Object.keys(e).length > 0) {
            const missing = Object.keys(e).map(fieldLabel).join("، ");
            toast.warn(
                (lang === "ar" ? "يرجى استكمال: " : "Please complete: ") + missing
            );
            return;
        }
        setSubmitting(true);

        let userID = "";
        try {
            const u = JSON.parse(getData(User_Data_Key, true));
            userID = u?.id?.toString().split("_")[1] || "";
        } catch { /* not logged in */ }

        // Always send a real ServiceID — the SP rejects MainServiceID GUIDs.
        // fetchServicesForVehicle walks the sub-service tree for body+paint,
        // so by step-3 we should always have a resolved ServiceID.
        const requiredService = serviceID || (serviceList[0] && serviceList[0].ServiceID);
        if (!requiredService) {
            setSubmitting(false);
            toast.error(lang === "ar"
                ? "تعذر تحديد خدمة مناسبة لهذه المركبة، حاول اختيار سيارة أخرى."
                : "Could not resolve a service for this vehicle, try a different model.");
            return;
        }
        const storeID = siteID || (branches[0] && branches[0].SiteID);

        const raw = {
            VIN: selectedVehicleVIN || "",
            Model: type,
            CarType: modelCode,
            UserID: userID,
            Year: year,
            TotalAmount: 0,
            RequiredServicesList: requiredService,
            Note: note,
            MeterReading: meterReading ? Number(meterReading) : null,
            FirstName: firstName,
            LastName: lastName,
            phone: phone,
            StoreID: storeID,
            Email: email,
            Orderdate: moment(date).format("YYYY-MM-DD"),
            OrderTime: moment(time).format("HH:mm:ss"),
            CopyformFile: null,
        };

        try {
            const res = await axios.request({
                url: `${config.controllURL}api/Service/ServiceRequestAdd`,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                data: raw,
                responseType: "json",
            });
            journifyTrackingCarEvents.trackGenerateLead(0);
            if (res?.data?.OrderID) setResultOrderID(res.data.OrderID);
            toast.success(trans("info:reservation_completed_successfully"));
            setStep(4);
        } catch (err) {
            console.error("ServiceRequestAdd failed:", err);
            toast.error(lang === "ar" ? "تعذر إكمال الحجز، حاول لاحقاً." : "Could not complete the booking, try later.");
        } finally {
            setSubmitting(false);
        }
    };

    // ============== derived ==============
    const filteredServices = useMemo(() =>
        (mainServices || []).filter((s) => {
            const ar = s.DescriptionAr?.trim();
            const en = s.DescriptionEn?.trim().toLowerCase();
            return ar !== "ميكانيكا" && en !== "mechanical";
        }), [mainServices]);

    const yearOptions = useMemo(() => {
        const years = (settings.models || [])
            .map((m) => m.modelID)
            .filter((m) => m?.startsWith("20"));
        return Array.from(new Set(years)).sort().reverse();
    }, [settings.models]);

    const reset = () => {
        setStep(1);
        setSelectedService(null);
        setSelectedSub1(null);
        setSubServices1([]);
        setBrand(null); setYear(null); setGroup(null); setType(null); setModelCode("");
        setSelectedVehicleVIN("");
        setActiveVehicleTab(isLogin && userVehicles.length > 0 ? "myCars" : "newCar");
        setServiceList([]); setServiceID(null);
        setSiteID(branches?.length === 1 ? branches[0].SiteID : null);
        setDate(null); setTime(null); setAvailableHours([]); setNote("");
        setFirstName(""); setLastName(""); setPhone(null); setEmail("");
        setResultOrderID(null);
        setErrors({});
    };

    // Extracted to plain variables — eslint-plugin-react-hooks v4.2.0 mis-parses
    // certain 3-term `&&` chains in JSX and reports every hook in the file as
    // "called conditionally". Keeping the JSX guards to 1-2 terms sidesteps it.
    const canPickRegistered = isLogin && userVehicles.length > 0;
    const showMyVehiclesPicker = canPickRegistered && activeVehicleTab === "myCars";
    const onStep1 = step === 1;
    const onSub1Picker = subServices1.length > 0;
    const sub1FromState = selectedSub1?.SubService1ID;
    const sub1FromService = selectedService?.SubService1ID;
    const effectiveSubServiceID = sub1FromState || sub1FromService || selectedService?.MainServiceID;
    const subTypeLabelAr = selectedSub1?.DescriptionAr || selectedSub1?.DescriptionEn || "";
    const subTypeLabel = lang === "ar" ? subTypeLabelAr : (selectedSub1?.DescriptionEn || "");

    // ============== render ==============
    return (
        <div className="v7-book-card-wrap" id="book-card">
            <div className="v7-book-card v7-fade-in">
                <div className="v7-progress-bar">
                    <div className={`v7-progress-dot ${step >= 1 ? "active" : ""}`} />
                    <div className={`v7-progress-dot ${step >= 2 ? "active" : ""}`} />
                    <div className={`v7-progress-dot ${step >= 3 ? "active" : ""}`} />
                    <div className={`v7-progress-dot ${step >= 4 ? "active" : ""}`} />
                </div>

                {/* === STEP 1: Service picker; switches to sub-service-1 picker
                       when the chosen main service has 2+ sub1 entries (body+paint). === */}
                {onStep1 && (
                    <div className="v7-fade-in">
                        {onSub1Picker ? (
                            <Step1SubServicePicker
                                lang={lang}
                                selectedService={selectedService}
                                subServices1={subServices1}
                                onBack={handleBackToServices}
                                onPick={handleSelectSub1}
                            />
                        ) : (
                            <Step1MainServicePicker
                                lang={lang}
                                services={filteredServices}
                                selectedService={selectedService}
                                loadingSub1={loadingSub1}
                                onPick={handleSelectService}
                            />
                        )}
                    </div>
                )}

                {/* === STEP 2: Vehicle === */}
                {step === 2 && (
                    <div className="v7-fade-in">
                        <button className="v7-step-back" onClick={handleBack} type="button">
                            {lang === "ar" ? "‹ رجوع" : "‹ Back"}
                        </button>
                        <h3 className="v7-step-title">
                            {lang === "ar"
                                ? selectedService?.DescriptionAr?.replace(/وميكانيكا|ميكانيكا/g, "").trim()
                                : selectedService?.DescriptionEn?.replace(/and mechanics|mechanics/gi, "").trim()}
                        </h3>
                        <p className="v7-step-sub">{lang === "ar" ? "اختر بيانات سيارتك" : "Pick your vehicle"}</p>

                        {canPickRegistered && (
                            <div className="v7-chip-grid" style={{ marginBottom: "1.2rem" }}>
                                <button
                                    type="button"
                                    className={`v7-chip ${activeVehicleTab === "myCars" ? "selected" : ""}`}
                                    onClick={() => handleVehicleTabChange("myCars")}
                                >
                                    {trans("info:reserve_for_my_vehicle")}
                                </button>
                                <button
                                    type="button"
                                    className={`v7-chip ${activeVehicleTab === "newCar" ? "selected" : ""}`}
                                    onClick={() => handleVehicleTabChange("newCar")}
                                >
                                    {trans("info:reserve_for_another_vehicle")}
                                </button>
                            </div>
                        )}

                        {showMyVehiclesPicker && (
                            <div className="v7-field">
                                <label>{trans("info:Vehicle-must-be-selected")}</label>
                                <select
                                    value={selectedVehicleVIN}
                                    onChange={(e) => handleSelectMyVehicle(e.target.value)}
                                >
                                    <option value="">{trans("info:Vehicle-must-be-selected")}</option>
                                    {userVehicles.map((v) => {
                                        const id = v.CarID || v.SN;
                                        const modelTrans = lang === "ar" ? "Ar" : "En";
                                        const carName = v[`ModelDescription${modelTrans}`] || v[`GroupDescription${modelTrans}`] || v.carAr || v.carEn || "";
                                        const brandName = v[`BrandDescription${modelTrans}`] || "";
                                        return (
                                            <option key={id} value={id}>{`${brandName} ${carName}`.trim() || id}</option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}

                        {activeVehicleTab === "newCar" && (
                            <div>
                                <div className="v7-field">
                                    <label>{trans("info:company")}</label>
                                    <div className="v7-grid-2">
                                        {settings.brands.map((b) => (
                                            <button
                                                key={b.brandID}
                                                type="button"
                                                className={`v7-brand-btn ${brand === b.brandID ? "selected" : ""}`}
                                                onClick={() => handleSelectBrand(b.brandID, year)}
                                            >
                                                {b.description}
                                            </button>
                                        ))}
                                    </div>
                                    {errors.brand && <span className="v7-field-error">{errors.brand}</span>}
                                </div>

                                <div className="v7-field">
                                    <label>{trans("info:created_year")}</label>
                                    <div className="v7-chip-grid">
                                        {yearOptions.slice(0, 12).map((y) => (
                                            <button
                                                key={y}
                                                type="button"
                                                className={`v7-chip ${year === y ? "selected" : ""}`}
                                                onClick={() => handleSelectYear(y)}
                                            >
                                                {y}
                                            </button>
                                        ))}
                                    </div>
                                    {errors.year && <span className="v7-field-error">{errors.year}</span>}
                                </div>

                                {brand && year && (
                                    <div className="v7-field">
                                        <label>{trans("eparts:vehicle")}</label>
                                        <select value={group || ""} onChange={(e) => handleSelectGroup(e.target.value)}>
                                            <option value="">{trans("eparts:vehicle")}</option>
                                            {filteredGroups.map((g) => (
                                                <option key={g.productGroupID} value={g.productGroupID}>{g.description}</option>
                                            ))}
                                        </select>
                                        {errors.group && <span className="v7-field-error">{errors.group}</span>}
                                    </div>
                                )}

                                {group && filteredModels.length > 0 && (
                                    <div className="v7-field">
                                        <label>{trans("eparts:model")}</label>
                                        <select value={type || ""} onChange={(e) => handleSelectType(e.target.value)}>
                                            <option value="">{trans("eparts:model")}</option>
                                            {filteredModels.map((m) => (
                                                <option key={m.productTypeID} value={m.productTypeID}>{m.ShortDescriptionEn}</option>
                                            ))}
                                        </select>
                                        {errors.type && <span className="v7-field-error">{errors.type}</span>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="v7-book-actions">
                            <button className="v7-btn-prev" onClick={handleBack} type="button">
                                {trans("info:back")}
                            </button>
                            <button className="v7-btn-next" onClick={handleNext} type="button">
                                {lang === "ar" ? "التالي" : "Next"} ›
                            </button>
                        </div>
                    </div>
                )}

                {/* === STEP 3: Date / Time / Note / Contact === */}
                {step === 3 && (
                    <div className="v7-fade-in">
                        <button className="v7-step-back" onClick={handleBack} type="button">
                            {lang === "ar" ? "‹ رجوع" : "‹ Back"}
                        </button>
                        <h3 className="v7-step-title">{lang === "ar" ? "موعد الحجز" : "Pick a Slot"}</h3>
                        <p className="v7-step-sub">{lang === "ar" ? "اليوم والوقت والملاحظات" : "Day, time and note"}</p>

                        {serviceList.length > 1 && (
                            <div className="v7-field">
                                <label>{trans("info:servce")}</label>
                                <select value={serviceID || ""} onChange={(e) => setServiceID(e.target.value)}>
                                    <option value="">{trans("info:servce")}</option>
                                    {serviceList.map((s) => (
                                        <option key={s.ServiceID} value={s.ServiceID}>
                                            {lang === "ar" ? s.ServiceNameAR : s.ServiceName}
                                        </option>
                                    ))}
                                </select>
                                {errors.serviceID && <span className="v7-field-error">{errors.serviceID}</span>}
                            </div>
                        )}

                        {branches.length > 1 && (
                            <div className="v7-field">
                                <label>{trans("info:branch")}</label>
                                <select value={siteID || ""} onChange={(e) => setSiteID(e.target.value)}>
                                    <option value="">{trans("info:branch")}</option>
                                    {branches.map((b) => (
                                        <option key={b.SiteID} value={b.SiteID}>
                                            {lang === "ar" ? b.DescriptionAr : b.DescriptionEn}
                                        </option>
                                    ))}
                                </select>
                                {errors.siteID && <span className="v7-field-error">{errors.siteID}</span>}
                            </div>
                        )}

                        <div className="v7-field">
                            <label>{trans("form:maintenance-date")}</label>
                            <button type="button" className="v7-svc-btn" style={{ justifyContent: "space-between" }} onClick={() => setShowCalendar(true)}>
                                <span>{date ? moment(date).format("YYYY-MM-DD") : trans("form:maintenance-date")}</span>
                                <span style={{ fontSize: "1.6rem" }}>📅</span>
                            </button>
                            {errors.date && <span className="v7-field-error">{errors.date}</span>}
                            <MaintenanceCalendarDialog
                                show={showCalendar}
                                onClose={() => setShowCalendar(false)}
                                selectedDate={date}
                                subServiceID={effectiveSubServiceID}
                                onSelect={(v) => { setShowCalendar(false); onDatePicked(v); }}
                            />
                        </div>

                        {date && (
                            <div className="v7-field">
                                <label>{trans("form:maintenance-time")}</label>
                                {availableHours.length > 0 ? (
                                    <div className="v7-time-grid">
                                        {availableHours.map((h) => {
                                            const t = new Date(date);
                                            t.setHours(h, 0, 0, 0);
                                            const sameDay = moment(date).isSame(moment(), "day");
                                            const disabled = sameDay && h <= new Date().getHours();
                                            const selected = time && new Date(time).getHours() === h;
                                            const label = `${h.toString().padStart(2, "0")}:00`;
                                            return (
                                                <button
                                                    key={h}
                                                    type="button"
                                                    disabled={disabled}
                                                    className={`v7-time-btn ${selected ? "selected" : ""}`}
                                                    onClick={() => {
                                                        setTime(t);
                                                        if ([13, 14, 15].includes(h)) {
                                                            toast.warn(lang === "ar"
                                                                ? "السيارة سيتم استلامها من قبل المهندس المختص وسيتم إعلامك بوقت الاستلام بعد الانتهاء من أعمال الصيانة"
                                                                : "Your car will be picked up by the technician; pickup time will be confirmed after maintenance.");
                                                        }
                                                    }}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: "1.2rem", color: "#999" }}>
                                        {lang === "ar" ? "لا توجد أوقات متاحة لهذا اليوم" : "No available times for this day"}
                                    </div>
                                )}
                                {errors.time && <span className="v7-field-error">{errors.time}</span>}
                            </div>
                        )}

                        <div className="v7-field">
                            <label>{trans("info:notes")}</label>
                            <textarea
                                rows={3}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={trans("info:notes")}
                            />
                        </div>

                        <div className="v7-field">
                            <label>{trans("info:meter-reading")}</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                value={meterReading}
                                onChange={(e) => setMeterReading(e.target.value.replace(/\D/g, ""))}
                                placeholder={trans("info:meter-reading")}
                            />
                        </div>

                        {/* Contact info — visible for everyone. For logged-in users the
                            values are pre-filled from their profile but stay editable so
                            the backend never gets empty FirstName/LastName/phone/Email. */}
                        <div className="v7-grid-2">
                            <div className="v7-field">
                                <label>{trans("form:first-name")}</label>
                                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                {errors.firstName && <span className="v7-field-error">{errors.firstName}</span>}
                            </div>
                            <div className="v7-field">
                                <label>{trans("info:surname")}</label>
                                <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                {errors.lastName && <span className="v7-field-error">{errors.lastName}</span>}
                            </div>
                        </div>
                        <div className="v7-field">
                            <label>{trans("form:phone")}</label>
                            <PhoneDropDown
                                title=""
                                extraClasses="phone-Input-module"
                                value={phone}
                                onChangeValue={(country, num) => {
                                    setPhone(num ? `+${country}${num}` : null);
                                    setCodephone(`+${country}`);
                                }}
                                defaultValue={phone?.replace(`${codephone}`, "")}
                            />
                            {errors.phone && <span className="v7-field-error">{errors.phone}</span>}
                        </div>
                        <div className="v7-field">
                            <label>{trans("form:email")}</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>

                        <div className="v7-summary-card">
                            <div className="v7-summary-row">
                                <span>{lang === "ar" ? "الخدمة" : "Service"}</span>
                                <strong>{lang === "ar" ? selectedService?.DescriptionAr : selectedService?.DescriptionEn}</strong>
                            </div>
                            {selectedSub1 && (
                                <div className="v7-summary-row">
                                    <span>{lang === "ar" ? "النوع" : "Type"}</span>
                                    <strong>{subTypeLabel}</strong>
                                </div>
                            )}
                            {brand && (
                                <div className="v7-summary-row">
                                    <span>{trans("info:company")}</span>
                                    <strong>{settings.brands.find((b) => b.brandID === brand)?.description}</strong>
                                </div>
                            )}
                            {year && (
                                <div className="v7-summary-row">
                                    <span>{trans("info:created_year")}</span>
                                    <strong>{year}</strong>
                                </div>
                            )}
                        </div>

                        <div className="v7-book-actions">
                            <button className="v7-btn-prev" onClick={handleBack} type="button">{trans("info:back")}</button>
                            <button className="v7-btn-submit" onClick={submit} disabled={submitting} type="button">
                                {submitting
                                    ? (lang === "ar" ? "جارٍ الإرسال..." : "Submitting...")
                                    : (lang === "ar" ? "أكمل الحجز الآن" : "Confirm Booking")}
                            </button>
                        </div>
                    </div>
                )}

                {/* === STEP 4: Success === */}
                {step === 4 && (
                    <div className="v7-fade-in" style={{ textAlign: "center" }}>
                        <div className="v7-success-circle">✓</div>
                        <h3 className="v7-step-title" style={{ fontSize: "2rem" }}>
                            {lang === "ar" ? "تم تأكيد الحجز!" : "Booking confirmed!"}
                        </h3>
                        <p className="v7-step-sub">
                            {lang === "ar" ? "سنتواصل معك لتأكيد التفاصيل" : "We will contact you to confirm details"}
                        </p>
                        {resultOrderID && (
                            <p style={{ fontSize: "1.2rem", color: "#666", marginBottom: "1.6rem" }}>
                                {lang === "ar" ? "رقم الطلب" : "Order #"}: <strong>{resultOrderID}</strong>
                            </p>
                        )}
                        <button className="v7-btn-next" style={{ width: "100%" }} onClick={reset} type="button">
                            {lang === "ar" ? "حجز خدمة أخرى" : "Book another service"}
                        </button>
                    </div>
                )}

                <div className="v7-book-trust">
                    <div className="v7-book-trust-item">🔒 {lang === "ar" ? "حجز آمن" : "Secure"}</div>
                    <div className="v7-book-trust-item">🛡 {lang === "ar" ? "ضمان على الخدمة" : "Service warranty"}</div>
                    <div className="v7-book-trust-item">⏱ {lang === "ar" ? "إلغاء مجاني" : "Free cancellation"}</div>
                </div>
            </div>
        </div>
    );
}

// Extracted children — keeps the parent's JSX flat so we don't trip the
// eslint-plugin-react-hooks v4.2.0 mis-parse that flags every hook as
// conditional when the parent has too many sibling `{cond && (...)}` blocks.
function Step1MainServicePicker({ lang, services, selectedService, loadingSub1, onPick }) {
    const chevron = lang === "ar" ? "‹" : "›";
    return (
        <div>
            <h3 className="v7-step-title">{lang === "ar" ? "اختر الخدمة" : "Choose the Service"}</h3>
            <p className="v7-step-sub">{lang === "ar" ? "خطوة واحدة فقط لإتمام الحجز" : "Just one step to book"}</p>
            <div className="v7-svc-list">
                {services.map((svc) => {
                    const title = lang === "ar"
                        ? svc.DescriptionAr?.replace(/وميكانيكا|ميكانيكا/g, "").trim()
                        : svc.DescriptionEn?.replace(/and mechanics|mechanics/gi, "").trim();
                    const isActive = selectedService?.MainServiceID === svc.MainServiceID;
                    const isLoadingThis = loadingSub1 && isActive;
                    return (
                        <button
                            key={svc.MainServiceID}
                            className={`v7-svc-btn ${isActive ? "selected" : ""}`}
                            onClick={() => onPick(svc)}
                            disabled={loadingSub1}
                            type="button"
                        >
                            <div className="v7-svc-icon">
                                <span
                                    className="v7-svc-icon-svg"
                                    style={{
                                        WebkitMaskImage: getServiceIcon(svc.DescriptionEn),
                                        maskImage: getServiceIcon(svc.DescriptionEn),
                                    }}
                                />
                            </div>
                            <span>{title}</span>
                            <span className="v7-svc-chevron">{isLoadingThis ? "…" : chevron}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function Step1SubServicePicker({ lang, selectedService, subServices1, onBack, onPick }) {
    const chevron = lang === "ar" ? "‹" : "›";
    const headTitle = lang === "ar"
        ? selectedService?.DescriptionAr?.replace(/وميكانيكا|ميكانيكا/g, "").trim()
        : selectedService?.DescriptionEn?.replace(/and mechanics|mechanics/gi, "").trim();
    return (
        <div>
            <button className="v7-step-back" onClick={onBack} type="button">
                {lang === "ar" ? "‹ رجوع" : "‹ Back"}
            </button>
            <h3 className="v7-step-title">{headTitle}</h3>
            <p className="v7-step-sub">{lang === "ar" ? "اختر نوع الخدمة" : "Choose the type"}</p>
            <div className="v7-svc-list">
                {subServices1.map((sub) => {
                    const arTitle = sub.DescriptionAr || sub.DescriptionEn;
                    const title = lang === "ar" ? arTitle : sub.DescriptionEn;
                    return (
                        <button
                            key={sub.SubService1ID}
                            className="v7-svc-btn"
                            onClick={() => onPick(sub)}
                            type="button"
                        >
                            <div className="v7-svc-icon">
                                <span
                                    className="v7-svc-icon-svg"
                                    style={{
                                        WebkitMaskImage: getServiceIcon(selectedService?.DescriptionEn),
                                        maskImage: getServiceIcon(selectedService?.DescriptionEn),
                                    }}
                                />
                            </div>
                            <span>{title}</span>
                            <span className="v7-svc-chevron">{chevron}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default InlineBookingCard;
